<script setup lang="ts">
/**
 * Meerregelig tekstveld. Vervangt `<v-input type="textarea">`.
 */
import { computed, ref, useSlots } from 'vue'
import VDisplayValue from './VDisplayValue.vue'
import VFormField from './VFormField.vue'
import type { DisplayableProps, FormFieldBaseProps } from './types'
import { controlClasses, useFieldErrors, useFormFieldBindings, useSplitAttrs } from './useFormField'

defineOptions({ inheritAttrs: false })

const modelValue = defineModel<string | null>()

const props = withDefaults(
    defineProps<
        FormFieldBaseProps &
            DisplayableProps & {
                // VInput gaf de placeholder nooit door aan de textarea: het was een prop, dus
                // hij zat ook niet in $attrs. Hier wordt hij wél getoond.
                placeholder?: string | null
            }
    >(),
    {
        placeholder: null,
        showErrors: true,
    }
)

const slots = useSlots()
const { rootAttrs, controlAttrs } = useSplitAttrs()
const errors = useFieldErrors(props)
const fieldBindings = useFormFieldBindings(props, errors)

const inputRef = ref<HTMLTextAreaElement | null>(null)

function focus(): void {
    inputRef.value?.focus()
}

defineExpose({ inputRef, focus })

const classes = computed<string>(() =>
    controlClasses({
        inputClasses: props.inputClasses,
        invalid: props.invalid,
        hasPrefix: !!slots.prefix,
        hasSuffix: !!slots.suffix,
    })
)
</script>

<template>
    <VDisplayValue
        v-if="displayAs != null"
        v-bind="$attrs"
        :value="modelValue"
        :type="displayAs"
        :layout="layout"
        :align-right="inputClasses?.includes('text-right') ?? false"
    >
        <template v-if="$slots.suffix" #suffix><slot name="suffix"></slot></template>
    </VDisplayValue>

    <VFormField v-else v-bind="{ ...fieldBindings, ...rootAttrs }">
        <template v-if="$slots['label-append']" #label-append>
            <slot name="label-append"></slot>
        </template>
        <template v-if="$slots.prefix" #prefix><slot name="prefix"></slot></template>

        <textarea
            v-bind="controlAttrs"
            ref="inputRef"
            v-model="modelValue"
            :id="id ?? name ?? undefined"
            :name="name ?? undefined"
            :placeholder="placeholder ?? undefined"
            :class="classes"
            :disabled="disabled"
        >
        </textarea>

        <template v-if="$slots.suffix" #suffix><slot name="suffix"></slot></template>
    </VFormField>
</template>
